import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ASNLoadDetailsRequest, ASNLoadDetailsResponse } from '../../screens/NavigatorReports/pages/AsnGap/api/asnOverviewKpiApi';

// ─── Constants ────────────────────────────────────────────────────────────────
// Defaults for the ASN Gap → Load Details tab's own pagination/sort/search
// state. Kept here (rather than in the dashboard component) so the slice is
// the single source of truth for both the API response and the request
// parameters that produced it.

export const LOAD_DETAILS_DEFAULT_SORT_BY = 'receivedDate';
export const LOAD_DETAILS_DEFAULT_SORT_DIRECTION: 'ASC' | 'DESC' = 'DESC';
export const LOAD_DETAILS_DEFAULT_PAGE_SIZE = 25;

// ─── State ────────────────────────────────────────────────────────────────────
// Stores the ASN Gap Load Details table's API response plus the
// pagination/sort/search state that produced it, in Redux. Keeping this here
// (instead of local component state) means the last-fetched data survives
// unmounts of AsnGapDashboard (e.g. navigating away and back), so the table
// can show cached data instantly instead of a blank/loading state, and the
// same state can be read/dispatched from anywhere without prop-drilling.

interface AsnLoadDetailsState {
  data: ASNLoadDetailsResponse | null;
  loading: boolean;
  error: string | null;
  pageNumber: number;
  pageSize: number;
  sortBy: string;
  sortDirection: 'ASC' | 'DESC';
  searchText: string;
  // Set once the first fetch (triggered by the Load Details tab's first
  // activation) has been kicked off — lets the dashboard avoid re-firing
  // that lazy initial request on every tab switch back to Load Details.
  hasLoadedOnce: boolean;
  // The exact request payload most recently sent to
  // `POST /api/asn/load-details/list` — i.e. the currently *applied*
  // filters/sort/search (not any not-yet-applied filter-dropdown edits).
  // The Export to Excel flow reads this from Redux to build its own
  // all-pages export request, so export always matches what's on screen.
  lastRequest: ASNLoadDetailsRequest | null;
}

const initialState: AsnLoadDetailsState = {
  data: null,
  loading: false,
  error: null,
  pageNumber: 1,
  pageSize: LOAD_DETAILS_DEFAULT_PAGE_SIZE,
  sortBy: LOAD_DETAILS_DEFAULT_SORT_BY,
  sortDirection: LOAD_DETAILS_DEFAULT_SORT_DIRECTION,
  searchText: '',
  hasLoadedOnce: false,
  lastRequest: null,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const asnLoadDetailsSlice = createSlice({
  name: 'asnLoadDetails',
  initialState,
  reducers: {
    setAsnLoadDetailsLoading(state) {
      state.loading = true;
      state.error = null;
    },

    setAsnLoadDetailsData(state, action: PayloadAction<ASNLoadDetailsResponse>) {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },

    setAsnLoadDetailsError(state, action: PayloadAction<string>) {
      // Never fall back to stale/fixture data — the table renders its own
      // empty/error state while data is null.
      state.data = null;
      state.loading = false;
      state.error = action.payload;
    },

    setAsnLoadDetailsPageNumber(state, action: PayloadAction<number>) {
      state.pageNumber = action.payload;
    },

    setAsnLoadDetailsPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.pageNumber = 1;
    },

    setAsnLoadDetailsSort(
      state,
      action: PayloadAction<{ sortBy: string; sortDirection: 'ASC' | 'DESC' }>,
    ) {
      state.sortBy = action.payload.sortBy;
      state.sortDirection = action.payload.sortDirection;
    },

    setAsnLoadDetailsSearchText(state, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },

    setAsnLoadDetailsHasLoadedOnce(state) {
      state.hasLoadedOnce = true;
    },

    setAsnLoadDetailsLastRequest(state, action: PayloadAction<ASNLoadDetailsRequest>) {
      state.lastRequest = action.payload;
    },

    resetAsnLoadDetails() {
      return initialState;
    },
  },
});

export const {
  setAsnLoadDetailsLoading,
  setAsnLoadDetailsData,
  setAsnLoadDetailsError,
  setAsnLoadDetailsPageNumber,
  setAsnLoadDetailsPageSize,
  setAsnLoadDetailsSort,
  setAsnLoadDetailsSearchText,
  setAsnLoadDetailsHasLoadedOnce,
  setAsnLoadDetailsLastRequest,
  resetAsnLoadDetails,
} = asnLoadDetailsSlice.actions;

export default asnLoadDetailsSlice.reducer;
